import React from "react";
import { Callout, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { EntityType } from "../../../../models/enums";
import styles from "./SyncSchedulePanel.module.scss";

interface IImpactSummary {
  addedEntities: EntityType[];
  removedEntities: EntityType[];
  missingDependencies: Array<{ missingDep: EntityType; requiredBy: EntityType[] }>;
  hasIssues: boolean;
}

interface IImpactAnalysisPanelProps {
  requiresEgress: boolean;
  hasChanges: boolean;
  impactSummary: IImpactSummary;
  getDisplayName: (entityType: EntityType) => string;
}

const ImpactAnalysisPanel: React.FC<IImpactAnalysisPanelProps> = ({
  requiresEgress,
  hasChanges,
  impactSummary,
  getDisplayName
}) => {
  if (!requiresEgress) {
    return (
      <div className={styles.emptyState}>
        Dependency analysis not applicable for ingress-only customers.
      </div>
    );
  }

  return (
    <>
      {/* Adding and Removing in two columns */}
      {hasChanges && (impactSummary.addedEntities.length > 0 || impactSummary.removedEntities.length > 0) && (
        <div className={styles.impactTwoColumns}>
          {impactSummary.addedEntities.length > 0 && (
            <div className={styles.impactColumn}>
              <div className={`${styles.impactColumnTitle} ${styles.impactColumnTitleAdding}`}>
                ✓ Adding ({impactSummary.addedEntities.length}):
              </div>
              <ul className={styles.impactList}>
                {impactSummary.addedEntities.map(et => (
                  <li key={et}>{getDisplayName(et)}</li>
                ))}
              </ul>
            </div>
          )}

          {impactSummary.removedEntities.length > 0 && (
            <div className={styles.impactColumn}>
              <div className={`${styles.impactColumnTitle} ${styles.impactColumnTitleRemoving}`}>
                ✗ Removing ({impactSummary.removedEntities.length}):
              </div>
              <ul className={styles.impactList}>
                {impactSummary.removedEntities.map(et => (
                  <li key={et}>{getDisplayName(et)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {impactSummary.missingDependencies.length > 0 && (
        <Callout intent={Intent.WARNING} icon={IconNames.WARNING_SIGN}>
          <div className={styles.dependencyTitle}>Missing Dependencies:</div>
          <div className={styles.dependencyGrid}>
            {impactSummary.missingDependencies.map(({ missingDep, requiredBy }) => (
              <div key={missingDep} className={styles.dependencyItem}>
                <strong>{getDisplayName(missingDep)}</strong> is required by:
                <ul className={styles.dependencyItemList}>
                  {requiredBy.map(entity => (
                    <li key={entity}>{getDisplayName(entity)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Callout>
      )}

      {!impactSummary.hasIssues && (
        <Callout intent={Intent.SUCCESS} icon={IconNames.TICK}>
          {hasChanges ? 'All changes are valid with no dependency issues.' : 'Current configuration has no dependency issues.'}
        </Callout>
      )}
    </>
  );
};

export default ImpactAnalysisPanel;
